<template>
  <Teleport to="body">
    <Transition name="hovercard">
      <div
        v-if="visible && activeUserId"
        ref="cardEl"
        class="chat-hovercard fixed z-[60] w-[264px] rounded-xl overflow-hidden shadow-2xl"
        :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
        role="dialog"
        :aria-label="card?.name || $t('chat.card.title')"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
      >
        <!-- Loading skeleton -->
        <div v-if="!card" class="p-4 flex items-center gap-3">
          <div class="w-12 h-12 rounded-full bg-white/10 animate-pulse"></div>
          <div class="flex-1 space-y-2">
            <div class="h-3 w-2/3 rounded bg-white/10 animate-pulse"></div>
            <div class="h-2 w-1/3 rounded bg-white/5 animate-pulse"></div>
          </div>
        </div>

        <template v-else>
          <!-- Header -->
          <div class="p-3.5 pb-3">
            <div class="flex items-start gap-3">
              <div class="relative shrink-0" :class="{ 'collab-glow': card.openForCollabs }">
                <img
                  :src="card.avatar || '/images/default-avatar.svg'"
                  :alt="card.name"
                  class="w-14 h-14 rounded-full object-cover"
                  :class="card.openForCollabs ? 'ring-2 ring-cyan-400/70 ring-offset-2 ring-offset-transparent' : ''"
                />
                <span
                  v-if="card.status !== 'hidden'"
                  class="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full ring-2 ring-[var(--chrome-bg,#0d0e12)]"
                  :class="dotClass(card.status)"
                  :title="$t('chat.status.self.' + (card.status === 'offline' ? 'online' : card.status))"
                ></span>
              </div>
              <div class="min-w-0 flex-1 pt-0.5">
                <div class="flex items-center gap-1.5 min-w-0">
                  <p class="text-[15px] leading-tight font-bold text-white truncate">{{ card.name }}</p>
                  <span v-if="isSelf" class="shrink-0 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/10 text-text-muted">
                    {{ $t('chat.online.you') }}
                  </span>
                </div>
                <div class="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span
                    class="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md"
                    :class="roleBadgeClass(card.role)"
                  >
                    {{ $t('chat.card.role', { role: roleLabel(card.role) }) }}
                  </span>
                  <span
                    v-if="card.openForCollabs"
                    class="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-cyan-400/15 text-cyan-300 border border-cyan-400/30"
                  >
                    {{ $t('chat.badges.collab') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Status line — 'hidden': self-registered viewers only see their
                 friends' presence (server/utils/chatPresenceScope.ts). -->
            <p v-if="card.status !== 'hidden'" class="mt-2.5 text-[11px] flex items-center gap-1.5">
              <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="dotClass(card.status)"></span>
              <span class="text-text-muted">
                <template v-if="card.status === 'offline'">
                  {{ card.lastSeen ? $t('chat.card.last_active', { ago: timeAgo(card.lastSeen) }) : $t('chat.presence.offline') }}
                </template>
                <template v-else>{{ $t('chat.status.self.' + card.status) }}</template>
              </span>
            </p>

            <!-- Custom status -->
            <p v-if="card.customStatus" class="mt-1.5 text-[12px] text-text-dim italic leading-snug break-words">
              “{{ card.customStatus }}”
            </p>

            <!-- Bio -->
            <p v-if="card.bio" class="mt-2 text-[12px] text-text-muted leading-snug break-words whitespace-pre-line line-clamp-4">
              {{ card.bio }}
            </p>
          </div>

          <!-- Actions -->
          <div v-if="!isSelf" class="px-3.5 py-2.5 border-t border-white/5 flex items-center gap-1.5">
            <template v-if="isFriend">
              <button type="button" class="hc-btn hc-btn--primary flex-1" :disabled="busy" @click="onMessage">
                <el-icon size="13"><ChatDotRound /></el-icon><span>{{ $t('chat.card.message') }}</span>
              </button>
              <button type="button" class="hc-btn hc-btn--ghost" :disabled="busy" :title="$t('chat.card.call')" @click="onCall">
                <el-icon size="14"><Phone /></el-icon>
              </button>
            </template>
            <template v-else-if="isIncoming">
              <button type="button" class="hc-btn hc-btn--primary flex-1" :disabled="busy" @click="onAccept">
                <el-icon size="13"><Check /></el-icon><span>{{ $t('chat.card.accept') }}</span>
              </button>
              <button type="button" class="hc-btn hc-btn--ghost flex-1" :disabled="busy" @click="onReject">
                {{ $t('chat.card.reject') }}
              </button>
            </template>
            <template v-else-if="isOutgoing">
              <span class="text-[11px] text-text-dim italic flex-1">{{ $t('chat.card.pending') }}</span>
              <button type="button" class="hc-btn hc-btn--ghost" :disabled="busy" @click="onCancel">
                {{ $t('chat.card.cancel') }}
              </button>
            </template>
            <template v-else>
              <button type="button" class="hc-btn hc-btn--primary flex-1" :disabled="busy" @click="onAddFriend">
                <el-icon size="13"><Plus /></el-icon><span>{{ $t('chat.card.add_friend') }}</span>
              </button>
            </template>
          </div>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { ChatDotRound, Phone, Check, Plus } from '@element-plus/icons-vue'
import { useHoverCard } from '~/composables/chat/useHoverCard'
import { useUserCards } from '~/composables/chat/useUserCards'
import { useFriends } from '~/composables/chat/useFriends'
import { useChatThreads } from '~/composables/chat/useChatThreads'
import { useChatUI } from '~/composables/chat/useChatUI'
import { useCall } from '~/composables/chat/useCall'
import { useTimeAgo } from '~/composables/chat/useTimeAgo'

const { user } = useUserSession()
const { visible, activeUserId, anchorRect, pinned, cancelHide, scheduleHide, hideNow } = useHoverCard()
const { getCard, cards } = useUserCards()
const { sendRequest, accept, reject, removeFriend } = useFriends()
const { startDM } = useChatThreads()
const { setTab, openDMThread } = useChatUI()
const { start: startCall } = useCall()
const { format: timeAgo } = useTimeAgo()

const myId = computed(() => String(user.value?.id || user.value?._id || ''))
const card = computed(() => (activeUserId.value ? (cards.get(activeUserId.value) || null) : null))
const isSelf = computed(() => !!card.value && card.value.id === myId.value)

const fr = computed(() => card.value?.friendship || null)
const isFriend = computed(() => fr.value?.status === 'accepted')
const isIncoming = computed(() => fr.value?.status === 'pending' && fr.value.direction === 'incoming')
const isOutgoing = computed(() => fr.value?.status === 'pending' && fr.value.direction === 'outgoing')

const busy = ref(false)

// Fetch the card whenever a new user is shown.
watch([visible, activeUserId], ([v, id]) => {
  if (v && id) getCard(id)
})

/* ── Positioning: open to the LEFT of the anchor, clamped to viewport ── */
const CARD_W = 264
const GAP = 12
const cardEl = ref(null)
const pos = ref({ top: 0, left: 0 })

function reposition() {
  const r = anchorRect.value
  if (!r || typeof window === 'undefined') return
  const vw = window.innerWidth
  const vh = window.innerHeight
  let left = r.left - CARD_W - GAP
  // Fallback: if there's no room on the left, open to the right.
  if (left < 8) left = Math.min(r.right + GAP, vw - CARD_W - 8)
  if (left < 8) left = 8
  const h = cardEl.value?.offsetHeight || 280
  let top = r.top
  if (top + h > vh - 8) top = vh - h - 8
  if (top < 8) top = 8
  pos.value = { top, left }
}

watch([visible, anchorRect, card], async () => {
  if (!visible.value) return
  await nextTick()
  reposition()
}, { deep: false })

function onResize() { if (visible.value) reposition() }

// Pinned (click/tap-opened) cards dismiss on an outside click or Escape.
// Hover-opened cards already self-dismiss via scheduleHide on mouseleave.
// The .online-row guard lets clicking ANOTHER row re-pin without first
// bouncing through a close (the row's own @click re-pins).
function onDocClick(e) {
  if (!pinned.value || !visible.value) return
  const el = cardEl.value
  const target = e.target
  const insideCard = el && target instanceof Node && el.contains(target)
  const onRow = target instanceof Element && !!target.closest('.online-row')
  if (!insideCard && !onRow) hideNow()
}
function onDocKey(e) {
  if (e.key === 'Escape' && visible.value) {
    hideNow()
    // Don't let the same Escape also collapse the chat panel.
    e.stopPropagation()
  }
}
onMounted(() => {
  window.addEventListener('resize', onResize)
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onDocKey)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onDocKey)
})

/* ── Actions ── */
async function onMessage() {
  if (!card.value || busy.value) return
  busy.value = true
  try {
    const threadId = await startDM(card.value.id)
    if (threadId) { setTab('dms'); openDMThread(threadId) }
    hideNow()
  } finally { busy.value = false }
}

async function onCall() {
  if (!card.value || busy.value) return
  busy.value = true
  try {
    const threadId = await startDM(card.value.id)
    if (threadId) {
      hideNow()
      await startCall(threadId, 'dm', 'audio')
    }
  } catch { /* call setup errors surface in the call UI */ } finally { busy.value = false }
}

async function refresh() {
  if (activeUserId.value) await getCard(activeUserId.value, { force: true })
}
async function onAddFriend() {
  if (!card.value || busy.value) return
  busy.value = true
  try { await sendRequest(card.value.id); await refresh() } finally { busy.value = false }
}
async function onAccept() {
  if (!fr.value || busy.value) return
  busy.value = true
  try { await accept(fr.value.id); await refresh() } finally { busy.value = false }
}
async function onReject() {
  if (!fr.value || busy.value) return
  busy.value = true
  try { await reject(fr.value.id); await refresh() } finally { busy.value = false }
}
async function onCancel() {
  if (!fr.value || busy.value) return
  busy.value = true
  try { await removeFriend(fr.value.id); await refresh() } finally { busy.value = false }
}

/* ── Presentation helpers ── */
function dotClass(status) {
  if (status === 'afk') return 'bg-amber-400'
  if (status === 'dnd') return 'bg-red-500'
  if (status === 'offline') return 'bg-text-dim/60'
  return 'bg-emerald-400'
}
function roleLabel(role) {
  const map = { superadmin: 'Admin', admin: 'Admin', label_manager: 'Label', ar: 'A&R', creator: 'Creator', customer: 'Member' }
  return map[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member')
}
function roleBadgeClass(role) {
  if (role === 'admin' || role === 'superadmin') return 'bg-primary/15 text-primary border border-primary/30'
  if (role === 'label_manager' || role === 'ar') return 'bg-violet-400/15 text-violet-300 border border-violet-400/30'
  if (role === 'creator') return 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/30'
  return 'bg-white/5 text-text-muted border border-white/10'
}
</script>

<style scoped>
.chat-hovercard {
  background: var(--chrome-bg, rgba(13, 14, 18, 0.92));
  backdrop-filter: blur(var(--chrome-blur, 24px));
  -webkit-backdrop-filter: blur(var(--chrome-blur, 24px));
  border: 1px solid var(--chrome-border, rgba(255, 255, 255, 0.08));
}

.hc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  transition: background-color 120ms ease, color 120ms ease, opacity 120ms ease;
  white-space: nowrap;
}
.hc-btn:disabled { opacity: 0.5; cursor: default; }
.hc-btn--primary { background: var(--primary, #00f2ff); color: #000; }
.hc-btn--primary:not(:disabled):hover { filter: brightness(1.08); }
.hc-btn--ghost { background: rgba(255, 255, 255, 0.05); color: var(--text-muted, #9aa0aa); }
.hc-btn--ghost:not(:disabled):hover { background: rgba(255, 255, 255, 0.1); color: #fff; }

/* Soft cyan pulse for "open for collabs" — mirrors ChatFriendRow. Respects
   reduced-motion via the global override in assets/css/main.css. */
.collab-glow { animation: collabGlow 2.8s ease-in-out infinite; border-radius: 9999px; }
@keyframes collabGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 242, 255, 0.28); }
  50%      { box-shadow: 0 0 0 6px rgba(0, 242, 255, 0.00); }
}

.hovercard-enter-active, .hovercard-leave-active { transition: opacity 140ms ease, transform 140ms ease; }
.hovercard-enter-from, .hovercard-leave-to { opacity: 0; transform: translateX(6px) scale(0.98); }

@media (prefers-reduced-motion: reduce) {
  .hovercard-enter-active, .hovercard-leave-active { transition: none; }
  .hovercard-enter-from, .hovercard-leave-to { transform: none; }
  .hc-btn { transition: none; }
  .collab-glow { animation: none; }
}
</style>
